import React, { useEffect, useRef, useState } from 'react';
import { CardContent, Grid, Typography, Dialog, DialogContent } from '@mui/material';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import { useCustomTheme } from '../../contexts/ThemeContext';

interface DescriptionAreaProps {
    description: string;
    removeMargin?: boolean;
}

const DescriptionArea: React.FC<DescriptionAreaProps> = ({
                                                             description,
                                                             removeMargin,
                                                         }) => {
    const { theme } = useCustomTheme();

    const contentRef = useRef<HTMLDivElement | null>(null);
    const [openImage, setOpenImage] = useState<string | null>(null);

    function sanitizeQuillHtml(data: string) {
        // Convert opening <ol><li data-list="bullet"> to <ul><li>
        let sanitizedHtml = data.replace(
            /<ol><li data-list="bullet">/g,
            '<ul><li>'
        );

        // Replace closing tags only for the unordered lists (bullets)
        sanitizedHtml = sanitizedHtml.replace(/<\/li><\/ol>/g, '</li></ul>');

        return sanitizedHtml;
    }

    // Attach click listeners to images inside the description
    useEffect(() => {
        const container = contentRef.current;
        if (!container) return;

        const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];

        const handleImageClick = (event: Event) => {
            const img = event.currentTarget as HTMLImageElement;
            if (img && img.src) {
                setOpenImage(img.src);
            }
        };

        images.forEach((img) => {
            img.style.cursor = 'pointer';
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.addEventListener('click', handleImageClick);
        });

        // Cleanup
        return () => {
            images.forEach((img) => {
                img.removeEventListener('click', handleImageClick);
            });
        };
    }, [description]);

    return (
        <>
            <Grid item xs={12} sm={12} md={12} lg={12}>
                <Card
                    sx={{
                        marginLeft: removeMargin ? -2 : 0,
                        marginTop: 2,
                        backgroundColor: 'transparent',
                        cursor: 'default',
                    }}
                >
                    <CardContent>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <Typography
                                ref={contentRef}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    marginBottom: -1,
                                    '& h1, & h2, & h3, & h4, & h5, & h6, & p, & ol, & ul': {
                                        margin: 0,
                                    },
                                    '& p': {
                                        fontSize: '0.8rem',
                                        lineHeight: 1.5,
                                    },
                                    '& a': {
                                        color: theme.palette.warning.main,
                                    },
                                    '& img': {
                                        borderRadius: 1,
                                        marginTop: 1,
                                        marginBottom: 1,
                                        display: 'block',
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeQuillHtml(description),
                                }}
                            />
                        </div>
                    </CardContent>
                </Card>
            </Grid>

            {/* Full-size image dialog */}
            <Dialog
                open={!!openImage}
                onClose={() => setOpenImage(null)}
                maxWidth="lg"
            >
                <DialogContent
                    sx={{
                        p: 0,
                        backgroundColor: 'black',
                    }}
                >
                    {openImage && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                overflow: 'hidden',
                            }}
                        >
                            <img
                                src={openImage}
                                alt=""
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DescriptionArea;
